import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../AppIcon';
import {
  getAccountingMarketingHeaderLinks,
  getHubNavItems,
  getSheetsMarketingHeaderLinks,
} from '../../content/hubLanding';
import AccountingMarketingNavActions from './AccountingMarketingNavActions';

const linkDark =
  'hub-font-title text-sm font-light tracking-wider text-white underline-offset-4 transition-colors hover:text-[#ffbe33] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#ffbe33] lg:text-base';

const linkDarkCompact =
  'hub-font-title text-xs font-light tracking-wider text-white underline-offset-4 transition-colors hover:text-[#ffbe33] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#ffbe33] lg:text-sm xl:text-base';

const mobileLinkDark =
  'hub-font-title block w-full px-2 py-3 text-left text-sm font-light tracking-wider text-white hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#ffbe33]';

const menuBtnDark =
  'p-2 text-white hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#ffbe33] shrink-0';

/**
 * @param {{
 *   items?: Array<{ key: string; label: string; path?: string; href?: string; external?: boolean }>;
 *   trailing?: React.ReactNode;
 *   trailingMobile?: React.ReactNode;
 *   navAriaLabel?: string;
 * }} props
 */
const HubHeader = ({
  items: itemsProp,
  trailing = null,
  trailingMobile = null,
  navAriaLabel = 'Product hub',
}) => {
  const [open, setOpen] = useState(false);
  const items = itemsProp ?? getHubNavItems();
  const manyLinks = items.length > 4;
  const navRowClass = manyLinks ? 'hidden lg:flex' : 'hidden md:flex';
  const menuBtnClass = manyLinks ? 'lg:hidden' : 'md:hidden';
  const mobilePanelClass = manyLinks ? 'lg:hidden' : 'md:hidden';

  const linkClass = manyLinks ? linkDarkCompact : linkDark;

  /** Wider gaps when 7+ marketing links (accounting) — avoid “bunched” nav next to Region/CTAs. */
  const gapClass = manyLinks ? 'gap-x-5 lg:gap-x-7 xl:gap-x-9' : 'gap-x-6 lg:gap-x-8';

  return (
    <header className="w-full">
      <div className="flex flex-wrap items-center justify-between gap-3 md:gap-4">
        <Link to="/" className="inline-flex shrink-0 items-center" aria-label="Neevii — product hub home">
          <img src="/assets/images/Neevii_Banner_logo.png" alt="Neevii" className="h-9 w-auto md:h-10" />
        </Link>

        <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-x-4 gap-y-2 lg:gap-x-6">
          <nav aria-label={navAriaLabel} className={`${navRowClass} min-w-0 items-center`}>
            <ul className={`flex flex-wrap items-center justify-end ${gapClass}`}>
              {items.map((item) => (
                <li key={item.key}>
                  {item.external ? (
                    <a href={item.href} className={linkClass}>
                      {item.label}
                    </a>
                  ) : (
                    <Link to={item.path} className={linkClass}>
                      {item.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          {trailing ? <div className="hidden shrink-0 items-center lg:flex">{trailing}</div> : null}
        </div>

        <button
          type="button"
          onClick={() => setOpen((x) => !x)}
          className={`${menuBtnDark} ${menuBtnClass}`}
          aria-expanded={open}
          aria-controls="hub-mobile-nav"
          aria-label={open ? 'Close menu' : 'Open menu'}
        >
          <Icon name={open ? 'X' : 'Menu'} size={22} />
        </button>
      </div>

      {open && (
        <nav
          id="hub-mobile-nav"
          className={`mt-4 border-t border-white/15 pt-4 ${mobilePanelClass}`}
          aria-label={`${navAriaLabel} mobile`}
        >
          <ul className="flex flex-col gap-1">
            {items.map((item) => (
              <li key={item.key}>
                {item.external ? (
                  <a href={item.href} className={mobileLinkDark} onClick={() => setOpen(false)}>
                    {item.label}
                  </a>
                ) : (
                  <Link to={item.path} className={mobileLinkDark} onClick={() => setOpen(false)}>
                    {item.label}
                  </Link>
                )}
              </li>
            ))}
          </ul>
          {trailingMobile ? <div>{trailingMobile}</div> : null}
        </nav>
      )}
    </header>
  );
};

/**
 * Sticky marketing bar: hub-black styling, original nav titles per surface.
 * @param {{ preset: 'accounting' | 'sheets' }} props
 */
export function HubMarketingNav({ preset }) {
  const items =
    preset === 'accounting' ? getAccountingMarketingHeaderLinks() : getSheetsMarketingHeaderLinks();
  const trailing =
    preset === 'accounting' ? <AccountingMarketingNavActions variant="desktop" /> : null;
  const trailingMobile =
    preset === 'accounting' ? <AccountingMarketingNavActions variant="mobile" /> : null;

  return (
    <div className="sticky top-0 z-50 border-b border-white/10 bg-black/60 px-5 py-3 backdrop-blur-md sm:px-8 md:px-12 md:py-3">
      <HubHeader
        items={items}
        trailing={trailing}
        trailingMobile={trailingMobile}
        navAriaLabel={preset === 'accounting' ? 'Marketing site' : 'Neevii Smart Expenses'}
      />
    </div>
  );
}

export default HubHeader;
